/**
 * Every API call the app makes, grouped by resource.
 *
 * Kept in one file so the surface stays visible: if an endpoint isn't here, no
 * screen is calling it, and drift against the Rails routes is easy to spot.
 */

import { api, download } from "./client";
import type {
  AdminSnapshot, Blueprint, BlueprintDetail, DashboardPayload, EventDetail, EventFormPayload,
  EventGalleryItem, EventSummary, GalleryPayload, GenerationHandle, GenerationStatusPayload,
  Order, OrderStatusPayload, OrderSummary, Organization, Pagination, PrintJob, PrintQueuePayload,
  EventMembership, EventMembershipsPayload, EventPermission,
  PurchaseResult, PurchaseState, Session, SharePayload, SnapshotResult, TestPromptResult,
  UploadedAsset, User, ValidationResult,
} from "../types/api";
import type { DraftConfig, EventConfig, PurchaseAction } from "../types/config";

export const authApi = {
  login: (email: string, password: string) =>
    api.post<Session>("/api/auth/login", { email, password }, { anonymous: true }),

  register: (payload: {
    organization_name: string; organization_slug: string; email: string; password: string;
  }) => api.post<Session>("/api/auth/register", payload, { anonymous: true }),

  me: () => api.get<{ user: User; organization: Organization | null }>("/api/auth/me"),

  logout: () => api.delete<{ message: string }>("/api/auth/logout"),

  forgotPassword: (email: string) =>
    api.post<{ message: string }>("/api/auth/password/forgot", { email }, { anonymous: true }),

  resetPassword: (token: string, password: string, passwordConfirmation: string) =>
    api.post<{ message: string }>(
      "/api/auth/password/reset",
      { token, password, password_confirmation: passwordConfirmation },
      { anonymous: true },
    ),
};

export const eventsApi = {
  /** Public booth config. `preview` layers the unpublished draft and needs auth. */
  config: (slug: string, preview = false) =>
    api.get<EventConfig>(
      `/api/events/${encodeURIComponent(slug)}/config${preview ? "?preview=true" : ""}`,
      { anonymous: !preview },
    ),

  list: () => api.get<EventSummary[]>("/api/admin/events"),
  get: (id: number) => api.get<EventDetail>(`/api/admin/events/${id}`),
  create: (event: EventFormPayload) => api.post<EventDetail>("/api/admin/events", { event }),
  update: (id: number, event: EventFormPayload) => api.patch<EventDetail>(`/api/admin/events/${id}`, { event }),
  archive: (id: number) => api.delete<{ message: string }>(`/api/admin/events/${id}`),

  /**
   * Saves and applies an edit. Config is live, so this *is* the change going
   * out. `applied: false` means validation rejected it — the live booth kept
   * the last good version and `errors` says what's wrong.
   */
  saveDraft: (id: number, config: DraftConfig) =>
    api.patch<{ success: boolean; applied: boolean; errors?: unknown }>(
      `/api/admin/events/${id}/draft`,
      { config },
    ),

  /** Turns the booth link on. Config is already live; this is availability. */
  publish: (id: number) =>
    api.post<{ success: boolean; status: string; booth_url?: string; slug?: string }>(
      `/api/admin/events/${id}/publish`,
    ),

  validate: (id: number, config?: DraftConfig) =>
    api.post<ValidationResult>(`/api/admin/events/${id}/validate_config`, config ? { config } : undefined),

  testPrompt: (id: number, payload: {
    participant_slug: string; scene_slug: string; prompt_template: string;
    negative_prompt: string; source_image: string;
  }) => api.post<TestPromptResult>(`/api/admin/events/${id}/test_prompt`, payload),

  importConfig: (id: number, yamlContent: string) =>
    api.post<{ success: boolean; message: string }>(`/api/admin/events/${id}/import_config`, {
      yaml_content: yamlContent,
    }),

  exportConfig: (id: number, slug: string) =>
    download(`/api/admin/events/${id}/export_config`, `${slug}-config.yml`),
};

export const generationsApi = {
  /**
   * Async by default: resolves to a handle the caller polls. `sync` is kept for
   * the admin prompt tester, where blocking is the desired behaviour.
   */
  create: (slug: string, payload: {
    scene_slug: string; participant_slug?: string | null; source_image: string;
    style_reference_image?: string;
  }) => api.post<GenerationHandle>(`/api/events/${encodeURIComponent(slug)}/generate`, payload, {
    anonymous: true,
  }),

  status: (slug: string, generationId: number) =>
    api.get<GenerationStatusPayload>(
      `/api/events/${encodeURIComponent(slug)}/generations/${generationId}/status`,
      { anonymous: true },
    ),

  eventGallery: (slug: string) =>
    api.get<EventGalleryItem[]>(`/api/events/${encodeURIComponent(slug)}/generations`, { anonymous: true }),

  share: (token: string) =>
    api.get<SharePayload>(`/api/generations/${encodeURIComponent(token)}`, { anonymous: true }),
};

export const snapshotApi = {
  /**
   * Captures a snapshot. Returns the record plus an order when the event
   * charges — the booth renders `order.checkout_url` as a QR for the guest to
   * scan with their own phone.
   */
  create: (slug: string, payload: {
    image: string; filter_slug?: string; guest_fields?: Record<string, string>;
    precomposed?: boolean; layout_id?: string;
  }) => api.post<SnapshotResult>(`/api/events/${encodeURIComponent(slug)}/snapshots`, payload, {
    anonymous: true,
  }),

  get: (slug: string, id: number) =>
    api.get<SnapshotResult>(`/api/events/${encodeURIComponent(slug)}/snapshots/${id}`, {
      anonymous: true,
    }),
};

/**
 * Buying an action on a finished photo.
 *
 * Capture is free; the guest pays to download, print or share. Each is a
 * separate purchase, so paying for a print does not grant the download.
 */
export const purchaseApi = {
  /** What this photo's actions cost, and which are already unlocked. */
  state: (slug: string, token: string) =>
    api.get<PurchaseState>(
      `/api/events/${encodeURIComponent(slug)}/purchases/${encodeURIComponent(token)}`,
      { anonymous: true },
    ),

  /** Opens an order. Returns `granted: true` with no order when it's free. */
  create: (slug: string, payload: {
    purchase_action: PurchaseAction;
    generation_id?: number;
    snapshot_id?: number;
    public_token?: string;
    /** Supplying a number switches telebirr to a USSD push. */
    payer_phone?: string;
  }) => api.post<PurchaseResult>(`/api/events/${encodeURIComponent(slug)}/purchases`, payload, {
    anonymous: true,
  }),

  /**
   * Settles an order without a payment provider. The server refuses this for
   * any order that went to a real provider, so it can't be used to skip paying.
   */
  simulate: (slug: string, reference: string) =>
    api.post<{ reference: string; status: string; paid: boolean; action: PurchaseAction }>(
      `/api/events/${encodeURIComponent(slug)}/purchases/${encodeURIComponent(reference)}/simulate`,
      undefined,
      { anonymous: true },
    ),

  /** The gated file. Only resolves once the download has been paid for. */
  downloadUrl: (slug: string, token: string) =>
    `/api/events/${encodeURIComponent(slug)}/download/${encodeURIComponent(token)}`,
};

export const paymentsApi = {
  /** Polled by the kiosk while the guest pays on their handset. */
  order: (reference: string) =>
    api.get<OrderStatusPayload>(`/api/payments/orders/${encodeURIComponent(reference)}`, {
      anonymous: true,
    }),
};

export const printApi = {
  create: (slug: string, payload: {
    generation_id?: number; snapshot_id?: number; public_token?: string; copies?: number;
  }) =>
    api.post<PrintJob>(`/api/events/${encodeURIComponent(slug)}/print`, payload, { anonymous: true }),

  get: (slug: string, id: number) =>
    api.get<PrintJob>(`/api/events/${encodeURIComponent(slug)}/print_jobs/${id}`, { anonymous: true }),

  claim: (slug: string, id: number) =>
    api.post<PrintJob>(`/api/events/${encodeURIComponent(slug)}/print_jobs/${id}/claim`, undefined, {
      anonymous: true,
    }),

  complete: (slug: string, id: number, failed = false, error?: string) =>
    api.post<PrintJob>(`/api/events/${encodeURIComponent(slug)}/print_jobs/${id}/complete`, { failed, error }, {
      anonymous: true,
    }),
};

/**
 * Who administers a given event.
 *
 * Nested under the event because access is granted per event, not per person:
 * "who can touch the Heineken activation?" is the question an org admin asks.
 * Only org admins and superadmins may call any of these.
 */
export const membershipsApi = {
  list: (eventId: number) =>
    api.get<EventMembershipsPayload>(`/api/admin/events/${eventId}/memberships`),

  add: (eventId: number, userId: number, permissions: EventPermission[]) =>
    api.post<EventMembership>(`/api/admin/events/${eventId}/memberships`, {
      user_id: userId,
      permissions,
    }),

  setPermissions: (eventId: number, membershipId: number, permissions: EventPermission[]) =>
    api.patch<EventMembership>(
      `/api/admin/events/${eventId}/memberships/${membershipId}`,
      { permissions },
    ),

  remove: (eventId: number, membershipId: number) =>
    api.delete<{ message: string }>(`/api/admin/events/${eventId}/memberships/${membershipId}`),
};

export const adminApi = {
  dashboard: () => api.get<DashboardPayload>("/api/admin/dashboard"),

  gallery: (params: {
    page?: number; per_page?: number; event_id?: number | string;
    provider?: string; search?: string; sort_by?: string; sort_order?: "asc" | "desc";
  }) => {
    const query = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== "" && value !== null) query.set(key, String(value));
    });
    return api.get<GalleryPayload>(`/api/admin/generations?${query.toString()}`);
  },

  exportGenerations: (payload: { generation_ids?: number[]; event_id?: number }) =>
    download("/api/admin/generations/export", `jeba-generations-${Date.now()}.zip`, {
      method: "POST",
      body: payload,
    }),

  printQueue: (params: { page?: number; status?: string; event_id?: number }) => {
    const query = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== "") query.set(key, String(value));
    });
    return api.get<PrintQueuePayload>(`/api/admin/print_jobs?${query.toString()}`);
  },

  retryPrintJob: (id: number) => api.post<PrintJob>(`/api/admin/print_jobs/${id}/retry`),

  uploadAsset: (file: File) => {
    const form = new FormData();
    form.append("file", file);
    return api.post<UploadedAsset>("/api/admin/assets/upload", form);
  },

  blueprints: {
    list: () => api.get<Blueprint[]>("/api/admin/blueprints"),
    get: (id: number) => api.get<BlueprintDetail>(`/api/admin/blueprints/${id}`),
    /** `fork_from` copies a system blueprint so it becomes editable. */
    create: (payload: { blueprint: Partial<BlueprintDetail>; fork_from?: string }) =>
      api.post<BlueprintDetail>("/api/admin/blueprints", payload),
    update: (id: number, blueprint: Partial<BlueprintDetail>) =>
      api.patch<BlueprintDetail>(`/api/admin/blueprints/${id}`, { blueprint }),
    remove: (id: number) => api.delete<{ message: string }>(`/api/admin/blueprints/${id}`),
  },

  orders: {
    list: (params: { page?: number; status?: string; event_id?: number }) => {
      const query = new URLSearchParams();
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== "") query.set(key, String(value));
      });
      return api.get<{ orders: Order[]; pagination: Pagination }>(
        `/api/admin/orders?${query.toString()}`,
      );
    },
    summary: () => api.get<OrderSummary>("/api/admin/orders/summary"),
  },

  snapshots: {
    list: (params: { page?: number; per_page?: number; event_id?: number; status?: string }) => {
      const query = new URLSearchParams();
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== "") query.set(key, String(value));
      });
      return api.get<{ snapshots: AdminSnapshot[]; pagination: Pagination }>(
        `/api/admin/snapshots?${query.toString()}`,
      );
    },
    remove: (id: number) => api.delete<{ message: string }>(`/api/admin/snapshots/${id}`),
  },

  users: {
    list: () => api.get<User[]>("/api/admin/users"),
    /** Creates the account outright. There are no invitation emails. */
    create: (user: { email: string; role?: string; password: string }) =>
      api.post<User>("/api/admin/users", { user }),
    /** Omit `password` to leave it unchanged; sending one revokes their sessions. */
    update: (id: number, user: { email?: string; role?: string; password?: string }) =>
      api.patch<User>(`/api/admin/users/${id}`, { user }),
    remove: (id: number) => api.delete<{ message: string }>(`/api/admin/users/${id}`),
  },

  organizations: {
    list: () => api.get<Organization[]>("/api/admin/organizations"),
    get: (id: number) => api.get<Organization>(`/api/admin/organizations/${id}`),
    update: (id: number, organization: Partial<Organization> & {
      /** Write-only. Merged server-side and never returned. */
      payment_credentials?: Record<string, string>;
    }) => api.patch<Organization>(`/api/admin/organizations/${id}`, { organization }),
    create: (organization: { name: string; slug: string }) =>
      api.post<Organization>("/api/admin/organizations", { organization }),
    remove: (id: number) => api.delete<{ message: string }>(`/api/admin/organizations/${id}`),
    telebirrH5: {
      token: (id: number) => api.post<{ token?: string; response: Record<string, unknown> }>(`/api/admin/organizations/${id}/telebirr_h5/token`, {}),
      order: (id: number, body: { token: string; title: string; amount: string; merchant_order_id?: string }) => api.post<Record<string, unknown>>(`/api/admin/organizations/${id}/telebirr_h5/order`, body),
      query: (id: number, body: { token: string; merchant_order_id: string }) => api.post<Record<string, unknown>>(`/api/admin/organizations/${id}/telebirr_h5/query`, body),
      refund: (id: number, body: { token: string; merchant_order_id: string; amount: string; reason?: string }) => api.post<Record<string, unknown>>(`/api/admin/organizations/${id}/telebirr_h5/refund`, body),
    },
  },
};
